import { Route, Routes } from 'react-router-dom';

import { AppLayout } from '@/components/layout/AppLayout';
import { ChatLayout } from '@/components/layout/ChatLayout';
import { PlaceholderPage } from '@/components/PlaceholderPage';
import { AddConnectorPage } from '@/features/connectors/AddConnectorPage';
import { ConnectorDetailPage } from '@/features/connectors/ConnectorDetailPage';
import { ConnectorsListPage } from '@/features/connectors/ConnectorsListPage';
import { DatasetDetailPage } from '@/features/connectors/DatasetDetailPage';
import { AssetDetailPage } from '@/features/catalog/AssetDetailPage';
import { CatalogLayout } from '@/features/catalog/CatalogLayout';
import { CatalogSearchPage } from '@/features/catalog/CatalogSearchPage';
import { EnrichmentReviewPage } from '@/features/catalog/EnrichmentReviewPage';
import { GlossaryPage } from '@/features/catalog/GlossaryPage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { DiagnosticsPage } from '@/features/diagnostics/DiagnosticsPage';
import { ChatPage } from '@/features/chat/ChatPage';
import { MdmPage } from '@/features/mdm/MdmPage';
import { QualityPage } from '@/features/quality/QualityPage';
import { ReportingPage } from '@/features/reporting/ReportingPage';
import { SchedulerPage } from '@/features/scheduler/SchedulerPage';
import { DataDomainsPage } from '@/features/domains/DataDomainsPage';
import { LoginPage } from '@/features/auth/LoginPage';
import { ProtectedRoute } from '@/features/auth/ProtectedRoute';

/**
 * Route table. Everything except /login is behind ProtectedRoute, so an
 * unauthenticated visitor is sent to the sign-in screen. The route set mirrors
 * the navigation registry; not-yet-built verticals render a PlaceholderPage.
 */
export function AppRouter(): JSX.Element {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      <Route element={<ProtectedRoute />}>
        {/* Standard pages: padded, scrollable content area */}
        <Route element={<AppLayout />}>
          <Route index element={<DashboardPage />} />
          <Route path="connectors">
            <Route index element={<ConnectorsListPage />} />
            <Route path="new" element={<AddConnectorPage />} />
            <Route path=":id" element={<ConnectorDetailPage />} />
          </Route>
          <Route path="datasets/:id" element={<DatasetDetailPage />} />
          <Route path="catalog" element={<CatalogLayout />}>
            <Route index element={<CatalogSearchPage />} />
            <Route path="asset" element={<AssetDetailPage />} />
            <Route path="glossary" element={<GlossaryPage />} />
            <Route path="enrichments" element={<EnrichmentReviewPage />} />
          </Route>
          <Route path="quality" element={<QualityPage />} />
          <Route path="mdm" element={<MdmPage />} />
          <Route path="data-domains" element={<DataDomainsPage />} />
          <Route path="reporting" element={<ReportingPage />} />
          <Route path="scheduler" element={<SchedulerPage />} />
          <Route
            path="observability"
            element={
              <PlaceholderPage
                title="Observability & Active Lineage"
                description="Real-time schema-drift monitoring and column-level lineage."
              />
            }
          />
          <Route
            path="privacy"
            element={
              <PlaceholderPage
                title="Privacy & Security"
                description="Automated PII identification and role-based dynamic masking."
              />
            }
          />
          <Route
            path="marketplace"
            element={
              <PlaceholderPage
                title="Data Marketplace"
                description="Self-service dataset requests, approvals, and usage analytics."
              />
            }
          />
          <Route path="diagnostics" element={<DiagnosticsPage />} />
          <Route path="*" element={<PlaceholderPage title="Not found" description="No such page." />} />
        </Route>

        {/* Chat: full-height layout, input pinned to bottom */}
        <Route element={<ChatLayout />}>
          <Route path="chat" element={<ChatPage />} />
        </Route>
      </Route>
    </Routes>
  );
}
